<template>
    <div id="quick-responses">
        <PreparedContentsToolbar v-model="searchText" :search-placeholder="$t('message.search')"
            :can-create-title="checkPermission(PERMISSION_TYPES['prepared.create'].tr.name)"
            :title-button-text="$t('message.newTitleContent')" :content-button-text="$t('message.newDefaultContent')"
            @create-title="openNewTitleFormModal"
            @create-content="$feature.beforeCreateReadyContent(openNewContentFormModal)" />

        <draggable class="pc-board" v-model="cloneGrouped" :options="{ handle: '.pcc-drag-handle' }"
            :disabled="isSearchActive" @start="handleCategoryDragStart" @end="handleCategoryDragEnd">
            <PreparedContentsCategoryCard v-for="(content, index) in cloneGrouped" :key="content._id || index"
                :content="content" :content-types="CONTENT_TYPES" :is-mac="isMac" :is-dark-mode="isDarkMode"
                :move="move" :get-content-name="getContentName" :get-children="getChildren"
                :can-create-items="checkPermission(PERMISSION_TYPES['prepared.create'].tr.name)"
                :can-delete-item="checkPermission(PERMISSION_TYPES['prepared.delete'].tr.name)"
                :can-delete-category="!content.hasOwnProperty('children') || content.children.length === 0"
                :alt-label="$t(isMac ? 'message.newCTRLContent' : 'message.newALTContent')"
                :tab-label="$t('message.newTABContent')" :hash-label="$t('message.newHASHContent')"
                :disabled="isSearchActive" @add-to-title="openAddContentToTitleFormModal" @edit="edit" @remove="remove"
                @move-end="moveEnd" />
        </draggable>
        <PreparedContentFormModal ref="preparedContentModal" :visible="isModalShow"
            :title="editMode ? $t('message.updateDefaultContentForm') : $t('message.newDefaultContentForm')"
            :content="content" :is-child="isChild" :edit-mode="editMode" :loading="loading"
            :submit-clicked="submitClicked" :is-mac="isMac" :contents-data="contents.data"
            :get-content-name="getContentName" :system-languages="systemLanguages" :default-lang="defaultLang"
            :can-update="checkPermission(PERMISSION_TYPES['prepared.update'].tr.name)" @cancel="hideModal"
            @submit="handleSave" @set-submit-clicked="submitClicked = $event" />
    </div>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../../../../components/common/AppBreadCrumb.vue";
import { log2 } from "../../../../../utils/log2";
import * as _ from "lodash";
import { CONTENT_TYPES } from "../../../../../utils/staticDatas";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import PreparedContentsToolbar from "./components/PreparedContentsToolbar.vue";
import PreparedContentsCategoryCard from "./components/PreparedContentsCategoryCard.vue";
import PreparedContentFormModal from "./components/PreparedContentFormModal.vue";
import { isMac } from "../../../../../utils/utils";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import SupsisButton from "../../components/SupsisButton.vue";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
const log = log2("Contents");
const contentInitialObject = {
    _id: null,
    parentId: null,
    type: undefined,
    letter: "",
    number: "",
    isActive: true,
    lastTargetId: undefined,
    languageContent: {},
};

export default {
    name: "Contents",
    components: {
        PreparedContentsToolbar,
        PreparedContentsCategoryCard,
        PreparedContentFormModal,
        SupsisButton,
        draggable,
        AppBreadCrumb,
    },
    data() {
        return {
            searchText: "",
            filteredData: [],
            index: null,
            isBusy: true,
            isChild: false,
            loading: false,
            editMode: false,
            submitClicked: false,
            CONTENT_TYPES,
            content: _.cloneDeep(contentInitialObject),
            perPage: 1000,
            page: 1,
            isMac,
            mentions: [
                { text: this.$t("message.name"), value: "name" },
                { text: this.$t("message.email"), value: "email" },
            ],
            showMentionList: false,
            mentionIndex: 0,
            selectedLang: null,
            contentTitle: "",
            cloneGrouped: [],
            isDragging: false,
            isModalShow: false,

            PERMISSION_TYPES,
        };
    },
    computed: {
        ...mapState({
            contents: (state) => state.content.contents,
            languages: (state) => state.language.languages,
            loggedUser: (state) => state.auth.user,
            isAdmin: (state) => state.auth.adminUser != null,
            site: (state) => state.site.site,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            grouped: "content/getGrouped",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        isSearchActive() {
            return !!(this.searchText && this.searchText.trim().length);
        },
        multiContentComp: {
            get() {
                log("systemLanguages", this.systemLanguages);
                return [
                    {
                        type: "input",
                        field: "name",
                        label: this.$t("message.titleContentForm"),
                        placeholder: this.$t("message.titleContentForm"),
                        data: (this.systemLanguages || []).reduce((acc, lang) => {
                            acc[lang] = _.get(this.content.languageContent, `[${lang}].name`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: (this.systemLanguages || []).reduce((acc, lang) => {
                            acc[lang] = _.get(this.content.languageContent, `[${lang}].content`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.content.languageContent = (this.systemLanguages || []).reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
    },
    methods: {
        ...mapActions({
            getContent: "content/getContent",
            createContent: "content/createContent",
            updateContent: "content/updateContent",
            updateParent: "content/updateParent",
            deleteContent: "content/deleteContent",
            getContents: "content/getContents",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        getContentName(child, readProperty = "name") {
            return (
                _.get(child, `languageContent.${this.$i18n.locale}.${readProperty}`) ||
                _.get(child, `languageContent.${Object.keys(child.languageContent)[0]}.${readProperty}`) ||
                ""
            );
        },
        openAddContentToTitleFormModal(parentContent, contentType) {
            this.editMode = false;
            this.isChild = false;
            this.content = {
                ..._.cloneDeep(contentInitialObject),
                type: contentType,
                parentId: parentContent._id,
            };
            /*this.$bvModal.show("content-form");*/
            this.showModal();
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        openNewTitleFormModal() {
            this.editMode = false;
            this.isChild = false;
            this.content = {
                ..._.cloneDeep(contentInitialObject),
                type: CONTENT_TYPES.TITLE,
            };
            /*this.$bvModal.show("content-form");*/
            this.showModal();
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        openNewContentFormModal() {
            this.editMode = false;
            this.isChild = true;
            /*this.$bvModal.show("content-form");*/
            this.showModal();
            setTimeout(() => {
                this.bindListener();
            }, 300);
        },
        get() {
            this.isBusy = true;
            return this.getContents({ perPage: this.perPage, page: this.page }).then(() => {
                this.isBusy = false;
                this.cloneGrouped = _.cloneDeep(this.grouped);
            });
        },
        handleCategoryDragStart() {
            this.isDragging = true;
        },
        handleCategoryDragEnd() {
            this.persistCategoryOrder();
            setTimeout(() => {
                this.isDragging = false;
            }, 50);
        },
        persistCategoryOrder() {
            if (!Array.isArray(this.cloneGrouped) || !this.cloneGrouped.length) return;
            const diffs = [];
            for (let i = 0; i < this.cloneGrouped.length; i++) {
                const item = this.cloneGrouped[i];
                if (!item) continue;
                if (item.order !== i) {
                    const updated = Object.assign({}, item, { order: i });
                    diffs.push(updated);
                    this.cloneGrouped[i].order = i;
                }
            }
            if (diffs.length) {
                diffs.forEach((x) => this.updateContent(x));
                setTimeout(() => {
                    sendSocketMessage("user content-moved", {
                        content: diffs,
                        siteId: this.site._id,
                    });
                }, 1000);
            }
        },
        edit(id, isChild = false) {
            this.getContent(id).then((response) => {
                this.editMode = true;
                this.isChild = isChild;
                this.content = _.cloneDeep(response);
                /*this.$bvModal.show("content-form");*/
                this.showModal();
                setTimeout(() => {
                    this.bindListener();
                }, 300);
                setTimeout(() => {
                    this.$validator.validateAll();
                }, 100);
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteContentQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteContent({ id }).then((res) => {
                        setTimeout(() => {
                            sendSocketMessage("user content-deleted", {
                                contentId: id,
                                siteId: this.site._id,
                            });
                        }, 1000);
                    });
                    this.$toasted.show(this.$t("message.defaultContentDeleted"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                });
        },
        reset() {
            this.content = _.cloneDeep(contentInitialObject);
            this.submitClicked = false;
            this.$validator.reset();
        },
        handleSave() {
            log.debug("handleSave");
            this.loading = true;

            const result = this.content._id
                ? this.updateContent(this.content).then((res) => {
                    setTimeout(() => {
                        sendSocketMessage("user content-updated", {
                            content: this.content,
                            siteId: this.site._id,
                        });
                    }, 1000);
                })
                : this.createContent(this.content).then((res) => {
                    setTimeout(() => {
                        sendSocketMessage("user new-content-added", {
                            content: this.content,
                            siteId: this.site._id,
                        });
                    }, 1000);
                });

            result
                .then(() => {
                    this.hideModal();
                    this.$toasted.show(this.$t("message.defaultContentSaved"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                    this.get();
                    this.reset();
                })
                .catch((err) => {
                    log.error(err.response, err.message);
                    const errMsg =
                        err.response && err.response.data && err.response.data.localizedMessages
                            ? err.response.data.localizedMessages[this.$i18n.locale]
                            : err.message;
                    this.$toasted.error(errMsg, {
                        duration: 4000,
                        position: "bottom-center",
                    });
                })
                .finally(() => {
                    this.loading = false;
                    this.submitClicked = false;
                });
        },
        async moveEnd(e) {
            try {
                const fromParentId = e.from && e.from.id;
                const toParentId = e.to && e.to.id;
                const movingItem = _.find(this.contents.data, (q) => q._id === e.item.id);

                if (!movingItem) return;

                const isParent = movingItem.parentId === null;
                const containsAnySubItem = this.getChildren(movingItem).length > 1;

                if (isParent && containsAnySubItem) {
                    this.$toasted.error(this.$t("message.cannotMoveFullParent"), {
                        duration: 2000,
                        position: "bottom-center",
                    });
                    await this.get();
                    this.cloneGrouped = _.cloneDeep(this.grouped);
                    return;
                }

                if (fromParentId === toParentId) {
                    this.persistChildrenOrder(fromParentId);
                    setTimeout(() => {
                        sendSocketMessage("user content-moved", {
                            content: movingItem,
                            siteId: this.site._id,
                        });
                    }, 1000);
                } else {
                    await this.updateParent({
                        _id: e.item.id,
                        parentId: toParentId,
                    });

                    this.persistChildrenOrder(fromParentId);
                    this.persistChildrenOrder(toParentId);

                    setTimeout(() => {
                        sendSocketMessage("user content-moved", {
                            content: movingItem,
                            siteId: this.site._id,
                        });
                    }, 1000);

                    this.$toasted.show(this.$t("message.contentMoved"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                }
            } catch (error) {
                console.error("Error moving content:", error);
                await this.get();
                this.cloneGrouped = _.cloneDeep(this.grouped);
            }
        },
        persistChildrenOrder(parentId) {
            if (!parentId) return;
            const parent = (this.cloneGrouped || []).find((x) => x && x._id === parentId);
            if (!parent || !Array.isArray(parent.children)) return;
            const diffs = [];
            for (let i = 0; i < parent.children.length; i++) {
                const child = parent.children[i];
                if (!child) continue;
                if (child.order !== i) {
                    diffs.push(Object.assign({}, child, { order: i, parentId: parentId }));
                    parent.children[i].order = i;
                }
            }
            diffs.forEach((x) => this.updateContent(x));
        },

        move(e) {
            if (e.dragged.id !== e.to.id) {
                const movingItem = _.find(this.contents.data, (q) => q._id === e.dragged.id);
                this.lastTargetId = e.to.id;
                if (movingItem) {
                    const isParent = movingItem.parentId === null;
                    const containsAnySubItem = this.getChildren(movingItem).length > 1;
                    log.debug("xdddd");
                    if (isParent && containsAnySubItem) {
                        return false;
                    } else {
                        return true;
                    }
                }
            }
        },
        getChildren(content) {
            const item = _.cloneDeep(content);
            let items = item.children || [];
            items.push({
                ...item,
                children: undefined,
            });
            items = _.uniqBy(items, (q) => q._id).filter((x) => x.type !== CONTENT_TYPES.TITLE);
            log.debug("getChildren", { item, items });
            return items;
        },
        bindListener() {
            let editors = document.querySelectorAll(".editr div[contenteditable=true]");

            let eventHandler = (e) => (event) => {
                let range = window.getSelection().getRangeAt(0);
                switch (event.key) {
                    case "@":
                        let mentions = document.querySelector(".mentions");
                        let modalRect = e.getBoundingClientRect();
                        let curRect = range.getBoundingClientRect();
                        mentions.style.left = `${curRect.left - modalRect.left}px`;
                        mentions.style.top = `${curRect.top - modalRect.top}px`;
                        this.showMentionList = true;
                        break;
                    case "ArrowDown":
                        if (!this.showMentionList) break;
                        this.mentionIndex =
                            this.mentionIndex === this.mentions.length - 1 ? this.mentionIndex : this.mentionIndex + 1;
                        event.preventDefault();
                        break;
                    case "ArrowUp":
                        if (!this.showMentionList) break;
                        this.mentionIndex = this.mentionIndex === 0 ? 0 : this.mentionIndex - 1;
                        event.preventDefault();
                        break;
                    case "Enter":
                        if (!this.showMentionList) break;
                        this.showMentionList = false;
                        let textNode = document.createTextNode(this.mentions[this.mentionIndex].value);
                        range.insertNode(textNode);
                        event.preventDefault();
                        break;
                    default:
                        this.showMentionList = false;
                        break;
                }
            };

            for (let e of editors) {
                e.addEventListener("keydown", eventHandler(e));
            }
        },
        handleFilter() {
            const filterChildren = this.contents.data.filter((d) => !d?.children);
            const result = [];
            if (this.searchText && this.searchText.trim().length > 0) {
                for (const data of filterChildren) {
                    const searchDataNameAndContent = Object.keys(data.languageContent).find(
                        (lc) =>
                            data.languageContent[lc].name.toLowerCase().includes(this.searchText.toLowerCase()) ||
                            data.languageContent[lc].content.toLowerCase().includes(this.searchText.toLowerCase())
                    );
                    if (searchDataNameAndContent) result.push(data);
                }
            }

            const resultHash = result.reduce((a, b) => {
                a[b._id] = true;
                return a;
            }, {});

            this.cloneGrouped = this.grouped.reduce((a, b) => {
                const searchChildren = b.children.filter((d) => resultHash[d._id]);
                if (!result.length || !!searchChildren.length) {
                    a.push({
                        ...b,
                        children: !searchChildren.length ? b.children : searchChildren,
                    });
                }
                return a;
            }, []);
        },
    },
    watch: {
        searchText() {
            this.handleFilter();
        },
        grouped: {
            deep: true,
            handler() {
                if (this.isDragging) return;
                if (this.isSearchActive) return;
                this.cloneGrouped = _.cloneDeep(this.grouped);
            },
        },
    },
    mounted() {
        this.$validator.localize(this.$i18n.locale);
        this.get();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
};
</script>

<style lang="scss" scoped>
.content {
    width: 85%;
    background: #eef0f8;
}

#quick-responses {
    min-height: 90vh;
}

.pc-board {
    display: grid;
    grid-template-columns: repeat(3, minmax(320px, 1fr));
    gap: 16px;
    margin-top: 16px;
}

@media (max-width: 1400px) {
    .pc-board {
        grid-template-columns: repeat(2, minmax(320px, 1fr));
    }
}

@media (max-width: 980px) {
    .pc-board {
        grid-template-columns: 1fr;
    }
}
</style>

<style lang="scss">
.custom-tabs .nav.nav-tabs {
    position: fixed;
    top: 92px;
    left: 92px;
    right: 30px;
    z-index: 123;
}

.mentions {
    z-index: 999999;
    background-color: #8787c7;
    color: #ffffff;
    padding: 0;
    margin: 0;
    list-style: none;
    border-radius: 0.6rem;
    display: inline-block;
    overflow: hidden;
    position: absolute;
    top: 70px;
    left: 10px;
    box-shadow: 0 0 3px #00000059;

    li {
        padding: 0.3rem 1.3rem;
        cursor: pointer;
        border-bottom: 1px solid #5a5a85;

        &:last-child {
            border-bottom: none;
        }

        &:hover,
        &.selected {
            background-color: #4f4fab;
        }
    }
}

.new-theme {
    #quick-responses {
        .custom-card {
            background-color: #ffffff;
            border-radius: 6px;
            padding: 10px;
            padding-top: 0;
            position: relative;
            border: 1px solid var(--color-border-default);

            min-height: 350px;
            height: 350px;
            max-height: 350px;

            .header {
                position: relative;
                padding-top: 25px;

                &:hover .hoverTools {
                    opacity: 1;
                    visibility: visible;
                    transform: translateY(0);
                }

                padding-top: 10px;
            }

            .hoverTools {
                position: absolute;
                bottom: 100%;
                left: 0;
                right: 0;
                margin: 20px;
                display: flex;
                justify-content: space-around;
                transition: all 0.3s ease;
                opacity: 0;
                visibility: hidden;
                transform: translateY(100%);
                padding: 10px 0;
            }

            .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 1px solid #3699ff;
                padding-bottom: 10px;

                span {
                    display: block;
                    font-family: Poppins;
                    font-size: 16px;
                    font-weight: 300;
                    color: #181c32;
                }

                .actions {
                    display: flex;

                    .actionButton {
                        border: none;
                        box-shadow: none;
                        padding: 0px 10px;
                    }
                }
            }

            .body {
                min-height: 250px;
                height: 250px;
                max-height: 250px;

                .item {
                    height: 30px;
                    margin: 8px 0;
                    border-radius: 6px;

                    cursor: move;

                    display: flex;
                    justify-content: space-between;

                    background-color: #ffffff;

                    .shortcuts {
                        margin-right: 6px;
                    }

                    .shortcut {
                        background-color: #eaecf2;
                        font-family: Poppins;
                        color: #3699ff;
                        font-size: 14px;
                        font-weight: 300;
                        text-align: center;
                        border-radius: 3px;
                        padding: 5px 5px 5px 5px;
                        white-space: nowrap;
                    }

                    .contentInfo {
                        color: #181c32;
                        font-size: 14px;
                        font-weight: 300;
                        padding-top: 5px;
                        display: block;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        padding-left: 5px;
                    }

                    .actions {
                        display: flex;

                        .actionButton {
                            border: none;
                            box-shadow: none;
                            height: 30px;
                        }
                    }
                }
            }
        }
    }
}

.fab {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    background-color: white;
    position: absolute;
    bottom: 2%;
    left: 16%;
    z-index: 9999;
    transition: all 0.3s;
    box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
    transform: scale(1);
    animation: pulse 3s infinite;
    cursor: pointer;
}

#content-form {
    #modal-inner {
        overflow-y: scroll;
        overflow-x: hidden;
        max-height: 70vh;
        padding: 0 20px;
    }

    .ant-modal-wrap {
        max-height: 100vh;
        /*overflow-y: hidden !important;*/
        /*top: -50px;*/
    }

    .ant-modal {
        width: 750px !important;
        padding-bottom: 0;
        top: 40px;
    }

    #buttons {
        display: flex;
        gap: 7px;
        justify-content: flex-end;
        padding-right: 20px;
    }
}

@keyframes pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
    }

    70% {
        transform: scale(1);
        box-shadow: 0 0 0 10px #00000000;
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 #00000000;
    }
}
</style>
